import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import MDBox from 'components/MDBox';
import { FormInputText } from 'components/Common/Forms/FormInputText';
import {
  renderButton,
  showLoader,
  handleBooleanProp,
  formatDateUTCString,
  useBackdropLoader,
  useNotification,
  getTimeSlotMessage,
  hasNoSlotsForDay,
} from 'utils/commonUtils';
import {
  CardContainer,
  GridWrapper,
  PGridCenter,
  FullCard,
  DashboardCard,
  DashboardSubHeader,
  CardWrapper,
  SubHeadingTypography,
  TitleTypography,
  TimeSlotGrid,
} from 'components/Ui/styled';
import { useDispatch, useSelector } from 'react-redux';
import OutlinedButtonComponent from 'components/Common/Buttons/OutlinedButtonComponent';
import {
  getTutoringSubjects,
  getTutoringAvailability,
  postTutoringAvailability,
} from '../services/tutoringApi';
import { getTutoringSubjectDetails } from '../store/tutoringSubjectsSlice/tutoringSubjectsSlice';
import { addDays } from 'date-fns';
import {
  setTutoringAvailabilityDetails,
  resetTutoringAvailabilityDetails,
} from '../store/tutoringAvailabilitySlice/tutoringAvailabilitySlice';
import { getDashboardDetails } from 'layouts/dashboards/store/dashboardSlice';
import { getDashboard } from 'layouts/dashboards/dashboard/services/dashboardApi';
import { selectSubjectDate, bookTutoringSessionConfirmation } from 'constants';
import ConfirmationDialog from 'components/Common/ConfirmationDialog/ConfirmationDialog';
import DashboardTutoringSessions from 'layouts/dashboards/dashboard/components/dashboardTutoringSessions/DashboardTutoringSessions';
// import { getMatchingSubjects } from '../utils/subjectMatching';

const defaultValue = {
  reason: '',
};

const TutoringComponent = () => {
  const dispatch = useDispatch();
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [disableButton, setDisableButton] = useState(true);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [isInfo, setIsInfo] = useState(false);

  const { setIsBackdropLoading, BackdropLoader } = useBackdropLoader();

  const {
    setOpenNotification,
    setNotificationMessage,
    NotificationPopup,
    handleErrorResponse,
  } = useNotification();

  const currentDate = new Date();

  const minDate = addDays(currentDate, 0);

  const subjects = useSelector((state) => state?.tutoringSubjects?.subjects);
  // const courses = useSelector((state) => state?.dashboard?.courses);

  // const matchedSubjects = useMemo(
  //   () => getMatchingSubjects(courses, subjects),
  //   [courses, subjects]
  // );
  const matchedSubjects = subjects;

  const tutoringSessions = useSelector(
    (state) => state?.dashboard?.tutoringSessions
  );
  const hasTutoringSessions = tutoringSessions?.length > 0;

  const timeSlots = useSelector(
    (state) => state?.tutoringAvailability?.timeSlots
  );

  const isSlots = hasNoSlotsForDay(timeSlots);

  useEffect(() => {
    const fetchTutoringSubjects = async () => {
      try {
        const tutoringSubjects = await getTutoringSubjects();
        dispatch(getTutoringSubjectDetails(tutoringSubjects));
        setIsLoading(false);
      } catch (error) {
        handleErrorResponse(error?.response?.data?.detail);
        setIsLoading(false);
        setIsBackdropLoading(false);
      }
    };
    fetchTutoringSubjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (matchedSubjects?.length === 1 && !selectedSubject) {
      handleSubject(matchedSubjects[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedSubjects]);

  useEffect(() => {
    if (selectedSubject && selectedDate && selectedTimeSlot) {
      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
  }, [selectedSubject, selectedDate, selectedTimeSlot]);

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
  });
  const { reset, control, handleSubmit } = useFunction;

  useEffect(() => {
    reset(defaultValue);
  }, [reset]);

  const handleSubject = async (subject) => {
    setSelectedSubject(subject.subjectId);
    if (subject.subjectId && selectedDate) {
      try {
        setIsBackdropLoading(true);
        const tutoringAvailability = await getTutoringAvailability(
          subject.subjectId,
          selectedDate
        );
        dispatch(
          setTutoringAvailabilityDetails(tutoringAvailability?.timeSlots)
        );
        setIsBackdropLoading(false);
        setIsInfo(true);
      } catch (error) {
        handleErrorResponse(error?.response?.data?.detail);
        setIsBackdropLoading(false);
      }
    }
  };

  const handleTimeSlot = (timeSlot) => {
    if (!timeSlot.available) return;
    if (selectedSubject && selectedDate) {
      setSelectedTimeSlot(timeSlot.timeSlotId);
    } else {
      setOpenNotification(true);
      setNotificationMessage(selectSubjectDate);
    }
  };

  const handleDateChange = async (date) => {
    const formattedDate = formatDateUTCString(date);
    setSelectedDate(formattedDate);
    if (selectedSubject && date) {
      try {
        setIsBackdropLoading(true);
        const tutoringAvailability = await getTutoringAvailability(
          selectedSubject,
          formattedDate
        );
        dispatch(
          setTutoringAvailabilityDetails(tutoringAvailability?.timeSlots)
        );
        setIsBackdropLoading(false);
        setIsInfo(true);
      } catch (error) {
        setIsInfo(false);
        handleErrorResponse(error?.response?.data?.detail);
        setIsBackdropLoading(false);
      }
    } else {
      setIsInfo(false);
      setOpenNotification(true);
      setNotificationMessage(selectSubjectDate);
    }
  };

  const handleSubmitButton = () => {
    setOpenConfirmationDialog(true);
  };

  const onSubmit = async (data) => {
    const { reason } = data;
    const payload = {
      date: selectedDate,
      reason: reason,
      timeSlotId: selectedTimeSlot,
    };
    try {
      setLoadingButton(true);
      await postTutoringAvailability(payload);
      setSelectedTimeSlot(null);
      const tutoringAvailability = await getTutoringAvailability(
        selectedSubject,
        selectedDate
      );
      const dashboardData = await getDashboard();
      dispatch(getDashboardDetails(dashboardData));
      dispatch(setTutoringAvailabilityDetails(tutoringAvailability?.timeSlots));
      setLoadingButton(false);
      setOpenConfirmationDialog(false);
    } catch (error) {
      const { detail, msg } = error?.response?.data ?? {};
      setLoadingButton(false);
      setSelectedTimeSlot(null);
      handleErrorResponse(detail || msg);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetTutoringAvailabilityDetails());
    };
  }, [dispatch]);

  const weekend = timeSlots?.length < 1;

  if (isLoading) {
    return showLoader();
  }

  return (
    <>
      {hasTutoringSessions && (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <DashboardSubHeader>
                <SubHeadingTypography variant="h6">
                  🎓 Scheduled Tutoring Sessions
                </SubHeadingTypography>
              </DashboardSubHeader>
              <DashboardCard style={{ height: '12.5vh', maxHeight: '12.5vh' }}>
                <DashboardTutoringSessions />
              </DashboardCard>
            </Grid>
          </Grid>
          <MDBox pt={3}></MDBox>
        </>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <DashboardSubHeader>
            <SubHeadingTypography variant="h6">
              Request a Tutoring Session - Choose a Subject
            </SubHeadingTypography>
          </DashboardSubHeader>
          <CardWrapper>
            <MDBox p={2}>
              <Grid container spacing={2}>
                {matchedSubjects?.map((subject) => (
                  <Grid item key={subject.subjectId}>
                    <OutlinedButtonComponent
                      isSubject={true}
                      handleClick={() => handleSubject(subject)}
                      selectedItem={selectedSubject}
                      item={subject.name}
                      id={subject.subjectId}
                    />
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          </CardWrapper>
        </Grid>
      </Grid>
      <MDBox pt={3}></MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <DashboardSubHeader>
            <SubHeadingTypography variant="h6">
              Select a Date
            </SubHeadingTypography>
          </DashboardSubHeader>
          <CardWrapper>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar onChange={handleDateChange} minDate={minDate} />
            </LocalizationProvider>
          </CardWrapper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <DashboardSubHeader>
            <SubHeadingTypography variant="h6">
              Select a Time Slot
            </SubHeadingTypography>
          </DashboardSubHeader>
          <FullCard>
            <Grid container>
              {timeSlots?.map((timeSlot, index) => (
                <PGridCenter
                  item
                  xs={12}
                  sm={4}
                  key={index}
                  primary={handleBooleanProp(!isInfo)}
                >
                  <OutlinedButtonComponent
                    handleClick={() => handleTimeSlot(timeSlot)}
                    selectedItem={selectedTimeSlot}
                    item={timeSlot.startTimeFmt}
                    id={timeSlot.timeSlotId}
                    available={timeSlot.available}
                  />
                </PGridCenter>
              ))}
            </Grid>
            <TimeSlotGrid container primary={handleBooleanProp(!isInfo || weekend || isSlots)}>
              <TitleTypography>
                {getTimeSlotMessage(timeSlots, isInfo, isSlots)}
              </TitleTypography>
            </TimeSlotGrid>
          </FullCard>
        </Grid>
      </Grid>
      <MDBox pt={3}></MDBox>
      <CardContainer>
        <GridWrapper container>
          <FormInputText
            name="reason"
            type="text"
            control={control}
            label="I need help with (please be as specific as possible):"
            multiline={true}
            rows={2}
          />
          {renderButton('Submit', handleSubmitButton, disableButton)}
        </GridWrapper>
      </CardContainer>
      <ConfirmationDialog
        openConfirmationDialog={openConfirmationDialog}
        setOpenConfirmationDialog={setOpenConfirmationDialog}
        title={bookTutoringSessionConfirmation}
        onSubmit={onSubmit}
        handleSubmit={handleSubmit}
        loadingButton={loadingButton}
      />
      <BackdropLoader />
      <NotificationPopup
        setOpenConfirmationDialog={setOpenConfirmationDialog}
      />
    </>
  );
};

export default TutoringComponent;
